// Show/hide the columns of a server-rendered table, remembering the choice in this browser.
// Markup comes from app/views/shared/_column_picker.html.haml:
//   [data-column-picker]            the picker, with data-column-picker-table (a selector for the
//                                   table) and data-column-picker-storage-key
//   input[data-column-key]          one checkbox per column, data-default-visible="true|false"
//   [data-column-picker-reset]      puts every column back to its default
// Every cell of the table carries data-column="<key>"; a hidden column's cells have .d-none.
//
// Only the columns the user has actually toggled are stored, as { key: visible }. A column with
// no stored choice keeps its default, so a field added to a form later still shows up when the
// server says it should.
// Turbolinks restores pages from cloned snapshots, so a restored picker is a new element with no
// listeners: track bound pickers by identity rather than with a data attribute that would be cloned.
const boundPickers = new WeakSet();
export const readChoices = (storageKey)=>{
    try {
        const parsed = JSON.parse(window.localStorage.getItem(storageKey));
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch  {
        return {};
    }
};
const writeChoices = (storageKey, choices)=>{
    try {
        if (Object.keys(choices).length === 0) {
            window.localStorage.removeItem(storageKey);
        } else {
            window.localStorage.setItem(storageKey, JSON.stringify(choices));
        }
    } catch  {
    // Storage unavailable (private mode, blocked site data): the toggle still applies to this page.
    }
};
const isDefaultVisible = (checkbox)=>checkbox.dataset.defaultVisible === 'true';
export const setColumnVisible = (table, key, visible)=>{
    table.querySelectorAll('[data-column]').forEach((cell)=>{
        if (cell.dataset.column === key) cell.classList.toggle('d-none', !visible);
    });
};
export const initColumnPicker = (picker)=>{
    if (boundPickers.has(picker)) return;
    const table = document.querySelector(picker.dataset.columnPickerTable);
    if (!table) return;
    boundPickers.add(picker);
    const { columnPickerStorageKey: storageKey } = picker.dataset;
    const checkboxes = [
        ...picker.querySelectorAll('input[data-column-key]')
    ];
    const choices = readChoices(storageKey);
    const apply = (checkbox, visible)=>{
        checkbox.checked = visible;
        setColumnVisible(table, checkbox.dataset.columnKey, visible);
    };
    checkboxes.forEach((checkbox)=>{
        const stored = choices[checkbox.dataset.columnKey];
        apply(checkbox, typeof stored === 'boolean' ? stored : isDefaultVisible(checkbox));
        checkbox.addEventListener('change', ()=>{
            const key = checkbox.dataset.columnKey;
            const current = readChoices(storageKey);
            if (checkbox.checked === isDefaultVisible(checkbox)) {
                delete current[key];
            } else {
                current[key] = checkbox.checked;
            }
            writeChoices(storageKey, current);
            setColumnVisible(table, key, checkbox.checked);
        });
    });
    picker.querySelector('[data-column-picker-reset]')?.addEventListener('click', ()=>{
        writeChoices(storageKey, {});
        checkboxes.forEach((checkbox)=>apply(checkbox, isDefaultVisible(checkbox)));
    });
};
const initColumnPickers = ()=>document.querySelectorAll('[data-column-picker]').forEach(initColumnPicker);
window.addEventListener('turbolinks:load', initColumnPickers);
